import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Check, X, Crown, Zap, Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Pricing() {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API}/pricing`);
        setPricing(response.data);
      } catch (error) {
        console.error('Error fetching pricing:', error);
      }
    };
    fetchPricing();
  }, []);

  const handleUpgrade = async (plan) => {
    if (!user) {
      toast.error('Please sign in first');
      navigate('/login');
      return;
    }

    if (user.is_premium) {
      toast.info('You already have premium!');
      return;
    }

    setLoading(true);
    setSelectedPlan(plan);

    try {
      // In production, this would integrate with Stripe
      // For now, simulate upgrade
      await axios.post(
        `${API}/auth/upgrade-premium?plan=${plan}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Welcome to Premium! 🎉`);
      await refreshUser();
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upgrade failed');
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  if (!pricing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-[#F5F5F5] mb-4">
            Choose Your Plan
          </h1>
          <p className="text-[#A3A3A3] text-lg max-w-2xl mx-auto">
            Unlock the full potential of Mixed Up with premium features
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6">
          {/* Free Plan */}
          <div className="glass rounded-2xl p-6 border border-white/10">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-[#F5F5F5] mb-2">Free</h3>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold text-[#F5F5F5]">$0</span>
                <span className="text-[#A3A3A3]">/forever</span>
              </div>
            </div>

            <ul className="space-y-3 mb-6">
              {pricing.free.features.map((feature, i) => (
                <li key={i} className="flex items-center gap-2 text-[#A3A3A3]">
                  <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
              {pricing.free.limitations.map((limitation, i) => (
                <li key={i} className="flex items-center gap-2 text-[#525252]">
                  <X className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <span>{limitation}</span>
                </li>
              ))}
            </ul>

            <Button
              variant="outline"
              className="w-full rounded-full border-white/20 text-[#A3A3A3]"
              disabled
            >
              Current Plan
            </Button>
          </div>

          {/* Monthly Plan */}
          <div className="glass rounded-2xl p-6 border-2 border-[#D4AF37] relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-black px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
              <Zap className="w-4 h-4" /> Popular
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-bold text-[#F5F5F5] mb-2 flex items-center gap-2">
                <Crown className="w-5 h-5 text-[#D4AF37]" /> Premium Monthly
              </h3>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold text-[#D4AF37]">${pricing.premium_monthly.price}</span>
                <span className="text-[#A3A3A3]">/month</span>
              </div>
            </div>

            <ul className="space-y-3 mb-6">
              {pricing.premium_monthly.features.map((feature, i) => (
                <li key={i} className="flex items-center gap-2 text-[#F5F5F5]">
                  <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <Button
              onClick={() => handleUpgrade('monthly')}
              disabled={loading || user?.is_premium}
              className="w-full bg-[#D4AF37] text-black hover:bg-[#F4CF57] font-bold rounded-full py-6 gold-glow"
              data-testid="upgrade-monthly-btn"
            >
              {loading && selectedPlan === 'monthly' ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : user?.is_premium ? (
                'Already Premium'
              ) : (
                'Subscribe Monthly'
              )}
            </Button>
          </div>

          {/* Lifetime Plan */}
          <div className="glass rounded-2xl p-6 border border-[#D4AF37]/50 relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#D4AF37] to-[#F4CF57] text-black px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1">
              <Star className="w-4 h-4" /> Best Value
            </div>

            <div className="mb-6">
              <h3 className="text-xl font-bold text-[#F5F5F5] mb-2 flex items-center gap-2">
                <Crown className="w-5 h-5 text-[#D4AF37]" /> Premium Lifetime
              </h3>
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-bold text-[#D4AF37]">${pricing.premium_lifetime.price}</span>
                <span className="text-[#A3A3A3]">one-time</span>
              </div>
              <p className="text-green-400 text-sm mt-1">Save ${(pricing.premium_monthly.price * 12 - pricing.premium_lifetime.price).toFixed(0)}/year!</p>
            </div>

            <ul className="space-y-3 mb-6">
              {pricing.premium_lifetime.features.map((feature, i) => (
                <li key={i} className="flex items-center gap-2 text-[#F5F5F5]">
                  <Check className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <Button
              onClick={() => handleUpgrade('lifetime')}
              disabled={loading || user?.is_premium}
              className="w-full bg-gradient-to-r from-[#D4AF37] to-[#F4CF57] text-black hover:opacity-90 font-bold rounded-full py-6"
              data-testid="upgrade-lifetime-btn"
            >
              {loading && selectedPlan === 'lifetime' ? (
                <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
              ) : user?.is_premium ? (
                'Already Premium'
              ) : (
                'Get Lifetime Access'
              )}
            </Button>
          </div>
        </div>

        {/* FAQ or Info */}
        <div className="mt-12 text-center text-[#A3A3A3]">
          <p>All plans include access to Bar Basics recipes and shopping suggestions.</p>
          <p className="mt-2 text-sm">Payments are processed securely. Cancel anytime.</p>
        </div>
      </div>
    </div>
  );
}
